import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import ValidationErrors from '@/components/validation-errors';
import type { Company } from '@/types/company';
import { Form } from '@inertiajs/react';
import { useState } from 'react';

interface Props {
  company: Pick<
    Company,
    'name' | 'isDefault' | 'githubOrgsText' | 'githubAccountsText' | 'githubRepoProjectMapText'
  > & { id: string | null };
  action: string;
  method: 'post' | 'patch';
  submitLabel: string;
}

export default function CompanyForm({ company, action, method, submitLabel }: Props) {
  const [isDefault, setIsDefault] = useState(company.isDefault);

  return (
    <Form method={method} action={action} className="space-y-6">
      {({ errors, processing }) => (
        <>
          <div className="space-y-2">
            <Label htmlFor="company_name">Name</Label>
            <Input
              id="company_name"
              name="company[name]"
              defaultValue={company.name ?? ''}
              autoFocus
              required
            />
            <ValidationErrors errors={errors.name} />
          </div>

          <div className="flex items-center gap-3">
            <input type="hidden" name="company[is_default]" value={isDefault ? '1' : '0'} />
            <Checkbox
              id="company_is_default"
              checked={isDefault}
              onCheckedChange={(checked) => setIsDefault(checked === true)}
            />
            <Label htmlFor="company_is_default">Default company</Label>
          </div>
          <ValidationErrors errors={errors.isDefault} />

          <div className="space-y-2">
            <Label htmlFor="company_github_orgs_text">GitHub orgs to watch</Label>
            <Input
              id="company_github_orgs_text"
              name="company[github_orgs_text]"
              defaultValue={company.githubOrgsText ?? ''}
              placeholder="wescom, acme-corp"
            />
            <p className="text-muted-foreground text-xs">
              Comma-separated org logins. PRs from these orgs land in Inbox.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="company_github_accounts_text">GitHub user accounts (optional)</Label>
            <Input
              id="company_github_accounts_text"
              name="company[github_accounts_text]"
              defaultValue={company.githubAccountsText ?? ''}
              placeholder="lance"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="company_github_repo_project_map_text">Repo → default project map</Label>
            <Textarea
              id="company_github_repo_project_map_text"
              name="company[github_repo_project_map_text]"
              defaultValue={company.githubRepoProjectMapText ?? ''}
              placeholder={'wescom/portal=<project-uuid>'}
              rows={4}
            />
            <p className="text-muted-foreground text-xs">One per line: owner/repo=project-id</p>
          </div>

          <Button type="submit" disabled={processing}>
            {submitLabel}
          </Button>
        </>
      )}
    </Form>
  );
}
